'use client';

/**
 * HomeFaq — executive-level FAQ accordion.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-016 (JSON-LD FAQPage feeder added in US-EP022-201/206 / S39.2)
 */

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';
import { Section } from '@/components/marketing/ui/section';
import { EyebrowBadge } from '@/components/marketing/ui/eyebrow';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

const FAQ_ITEMS = ['results', 'seo', 'methodology', 'stack', 'engagement'];

export function HomeFaq() {
  const t = useTranslations('marketing.home.faq');
  const [openKey, setOpenKey] = useState<string | null>('results');

  return (
    <Section id="faq">
      <MotionReveal>
        <EyebrowBadge>{t('eyebrow')}</EyebrowBadge>
        <h2 className="text-display mt-4 max-w-3xl text-balance text-3xl font-bold lg:text-5xl">
          {t('title')}
        </h2>
      </MotionReveal>

      <div className="mx-auto mt-12 max-w-3xl space-y-3 lg:mt-16">
        {FAQ_ITEMS.map((key) => {
          const isOpen = openKey === key;
          return (
            <div key={key} className="glass-card overflow-hidden !p-0">
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : key)}
                aria-expanded={isOpen}
                aria-controls={`faq-panel-${key}`}
                className="flex w-full items-center justify-between gap-4 p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 lg:p-6"
              >
                <span className="text-base font-semibold text-white lg:text-lg">
                  {t(`items.${key}.q`)}
                </span>
                <ChevronDown
                  size={20}
                  aria-hidden="true"
                  className={cn(
                    'shrink-0 text-cyan-300 transition-transform duration-200',
                    isOpen && 'rotate-180'
                  )}
                />
              </button>
              <div
                id={`faq-panel-${key}`}
                role="region"
                hidden={!isOpen}
                className="px-5 pb-5 text-sm leading-relaxed text-white/75 lg:px-6 lg:pb-6 lg:text-base"
              >
                {t(`items.${key}.a`)}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
